import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Pencil, Plus, Trash2, UserPlus } from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { CatalogSettings } from '../components/CatalogSettings';
import { SoftwareSettings } from '../components/SoftwareSettings';
import { CheckoutSettingsPanel } from '../components/checkout-settings';
import { DamageBillingSettings } from '../components/damage-billing-settings';
import { EmailTemplateSettings } from '../components/email-template-settings';
import { SignInSettings } from '../components/SignInSettings';
import { TemporaryPasswordNotice } from '../components/temporary-password';
import { useToast } from '../components/toast';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Modal,
  PageHeader,
  PageLoader,
  Pagination,
  Select,
  Table,
  Tabs,
  Td,
  Textarea,
  Th,
} from '../components/ui';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { FACILITIES_ROLE_DESCRIPTION, FACILITIES_ROLE_LABEL } from '../lib/facilities';
import { formatDateTime, formatRelative, ROLE_DESCRIPTION, ROLE_LABEL } from '../lib/format';
import { useCategories, useDebouncedValue, useUrlState } from '../lib/hooks';
import type { AuditEntry, Category, FacilitiesRole, Membership, Page, TenantRole, TenantSettings } from '../lib/types';

// ---------------------------------------------------------------------------
// School
// ---------------------------------------------------------------------------

function SchoolSettings() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const isAdmin = can('ADMIN');
  const tenant = useQuery({ queryKey: ['tenant'], queryFn: () => api.get<TenantSettings>('/tenant') });
  const [form, setForm] = useState({ name: '', timezone: '', contactEmail: '', assetTagPrefix: '', defaultCheckoutDays: '14' });
  const timezones = useMemo(() => Intl.supportedValuesOf('timeZone'), []);

  useEffect(() => {
    if (tenant.data) {
      setForm({
        name: tenant.data.name,
        timezone: tenant.data.timezone,
        contactEmail: tenant.data.contactEmail ?? '',
        assetTagPrefix: tenant.data.assetTagPrefix,
        defaultCheckoutDays: String(tenant.data.defaultCheckoutDays),
      });
    }
  }, [tenant.data]);

  const save = useMutation({
    mutationFn: () => api.patch('/tenant', { ...form, defaultCheckoutDays: Number(form.defaultCheckoutDays) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tenant'] });
      void queryClient.invalidateQueries({ queryKey: ['me'] });
      void queryClient.invalidateQueries({ queryKey: ['public-school'] });
      toast.success('School settings saved');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (tenant.isLoading) return <PageLoader />;
  if (tenant.error) return <ErrorState error={tenant.error} />;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate();
  };

  return (
    <Card>
      <CardHeader title="School profile" description={`${window.location.host}/${tenant.data!.slug}`} />
      <form onSubmit={submit} className="grid max-w-3xl gap-5 p-5 sm:grid-cols-2">
        <Field label="School name" required>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} disabled={!isAdmin} required />
        </Field>
        <Field label="Technology contact email">
          <Input type="email" value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} disabled={!isAdmin} />
        </Field>
        <Field label="Time zone">
          <Select value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} disabled={!isAdmin}>
            {timezones.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Asset tag prefix" hint={`Next tag: ${form.assetTagPrefix}${String(tenant.data!.nextAssetNumber).padStart(5, '0')}`}>
            <Input value={form.assetTagPrefix} onChange={(e) => setForm({ ...form, assetTagPrefix: e.target.value })} disabled={!isAdmin} maxLength={10} className="font-mono" />
          </Field>
          <Field label="Default loan (days)">
            <Input type="number" min={1} max={3650} value={form.defaultCheckoutDays} onChange={(e) => setForm({ ...form, defaultCheckoutDays: e.target.value })} disabled={!isAdmin} />
          </Field>
        </div>
        {isAdmin && (
          <div className="sm:col-span-2">
            <Button type="submit" variant="primary" loading={save.isPending}>
              Save
            </Button>
          </div>
        )}
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Campus network
// ---------------------------------------------------------------------------

function CampusNetworkSettings() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const isAdmin = can('ADMIN');
  const tenant = useQuery({ queryKey: ['tenant'], queryFn: () => api.get<TenantSettings>('/tenant') });
  const [ips, setIps] = useState('');

  useEffect(() => {
    if (tenant.data) setIps(tenant.data.campusPublicIps.join(', '));
  }, [tenant.data]);

  const save = useMutation({
    mutationFn: () => api.patch<TenantSettings>('/tenant', { campusPublicIps: ips }),
    onSuccess: (saved) => {
      queryClient.setQueryData(['tenant'], saved);
      toast.success('Campus IP addresses saved');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  // The school profile shows loading and errors.
  if (!tenant.data) return null;

  return (
    <Card>
      <CardHeader
        title="Campus network"
        description="Google Workspace and Jamf School compare a device’s public IP address with these to show whether it was on or off campus."
      />
      <div className="max-w-3xl space-y-4 p-5">
        <Field
          label="School public IP addresses"
          hint="Comma separated, e.g. 203.0.113.10, 203.0.113.11. CIDR ranges like 203.0.113.0/28 also work. Your network team or firewall can tell you these."
        >
          <Textarea
            rows={3}
            value={ips}
            onChange={(e) => setIps(e.target.value)}
            disabled={!isAdmin}
            className="font-mono text-xs"
            placeholder="203.0.113.10, 203.0.113.11"
          />
        </Field>
        {isAdmin && (
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
            Save IP addresses
          </Button>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------

function CategorySettings() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const categories = useCategories();
  const [editing, setEditing] = useState<Partial<Category> | null>(null);
  const [deleting, setDeleting] = useState<Category | null>(null);
  const [form, setForm] = useState({ name: '', description: '', defaultCheckoutDays: '' });

  useEffect(() => {
    if (editing) {
      setForm({ name: editing.name ?? '', description: editing.description ?? '', defaultCheckoutDays: editing.defaultCheckoutDays ? String(editing.defaultCheckoutDays) : '' });
    }
  }, [editing]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['categories'] });
  const save = useMutation({
    mutationFn: () => (editing?.id ? api.patch(`/categories/${editing.id}`, form) : api.post('/categories', form)),
    onSuccess: () => {
      void refresh();
      setEditing(null);
      toast.success('Category saved');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/categories/${deleting!.id}`),
    onSuccess: () => {
      void refresh();
      setDeleting(null);
      toast.success('Category deleted');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Card>
      <CardHeader
        title="Asset categories"
        actions={
          <Button size="sm" variant="primary" icon={<Plus />} onClick={() => setEditing({})}>
            Add category
          </Button>
        }
      />
      {categories.isLoading ? (
        <PageLoader />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Name</Th>
              <Th>Description</Th>
              <Th>Default loan</Th>
              <Th>Assets</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {categories.data?.map((category) => (
              <tr key={category.id}>
                <Td className="font-medium text-slate-900">{category.name}</Td>
                <Td className="text-slate-500">{category.description ?? '—'}</Td>
                <Td>{category.defaultCheckoutDays ? `${category.defaultCheckoutDays} days` : '—'}</Td>
                <Td>{category.assetCount}</Td>
                <Td className="text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" icon={<Pencil />} onClick={() => setEditing(category)}>
                    Edit
                  </Button>
                  <Button size="sm" variant="ghost" icon={<Trash2 />} onClick={() => setDeleting(category)}>
                    Delete
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        size="sm"
        title={editing?.id ? 'Edit category' : 'Add category'}
        footer={
          <>
            <Button onClick={() => setEditing(null)}>Cancel</Button>
            <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Name" required>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus maxLength={120} />
          </Field>
          <Field label="Description">
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={500} rows={2} />
          </Field>
          <Field label="Default loan length (days)" hint="Optional">
            <Input type="number" min={1} value={form.defaultCheckoutDays} onChange={(e) => setForm({ ...form, defaultCheckoutDays: e.target.value })} />
          </Field>
        </div>
      </Modal>
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => remove.mutate()}
        loading={remove.isPending}
        title={`Delete ${deleting?.name}?`}
        description={`${deleting?.assetCount ?? 0} assets will become uncategorized.`}
        confirmLabel="Delete"
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Users
// ---------------------------------------------------------------------------

const FACILITIES_ROLES: FacilitiesRole[] = ['ADMIN', 'TECHNICIAN', 'VIEWER'];

function UserSettings() {
  const { me } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get<Membership[]>('/users') });
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ email: '', name: '', role: 'TECHNICIAN' as TenantRole, facilitiesRole: '' as FacilitiesRole | '' });
  const [removing, setRemoving] = useState<Membership | null>(null);
  const [tempPassword, setTempPassword] = useState<{ email: string; password: string } | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['users'] });
  const googleSchool = !!me?.tenant?.googleSignIn;
  const passwordsAllowed = me?.tenant?.passwordSignIn ?? true;
  const facilitiesOn = !!me?.tenant?.facilitiesEnabled;

  const add = useMutation({
    mutationFn: () =>
      api.post<{ membership: Membership; temporaryPassword?: string; signsInWithGoogle?: boolean }>('/users', {
        email: form.email,
        name: form.name,
        role: form.role,
        facilitiesRole: facilitiesOn && form.role !== 'ADMIN' ? form.facilitiesRole || null : undefined,
      }),
    onSuccess: (result) => {
      void refresh();
      setAdding(false);
      setForm({ email: '', name: '', role: 'TECHNICIAN', facilitiesRole: '' });
      if (result.temporaryPassword) setTempPassword({ email: result.membership.user.email, password: result.temporaryPassword });
      else if (result.signsInWithGoogle) toast.success(`${result.membership.user.email} can now sign in with Google`);
      else toast.success(`${result.membership.user.email} now has access`);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const changeRole = useMutation({
    mutationFn: ({ id, role, facilitiesRole }: { id: string; role?: TenantRole; facilitiesRole?: FacilitiesRole | null }) =>
      api.patch(`/users/${id}`, { role, facilitiesRole }),
    onSuccess: () => {
      void refresh();
      toast.success('Role updated');
    },
    onError: (err) => {
      void refresh();
      toast.error(errorMessage(err));
    },
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/users/${removing!.id}`),
    onSuccess: () => {
      void refresh();
      setRemoving(null);
      toast.success('Access removed');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const reset = useMutation({
    mutationFn: (membership: Membership) =>
      api.post<{ temporaryPassword: string }>(`/users/${membership.id}/reset-password`).then((r) => ({ ...r, email: membership.user.email })),
    onSuccess: (result) => setTempPassword({ email: result.email, password: result.temporaryPassword }),
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Card>
      <CardHeader
        title="Users & roles"
        description={googleSchool ? 'People who can sign in to this school. They sign in with their school Google account.' : 'Staff who can sign in to this school’s environment'}
        actions={
          <Button size="sm" variant="primary" icon={<UserPlus />} onClick={() => setAdding(true)}>
            Add user
          </Button>
        }
      />
      {users.isLoading ? (
        <PageLoader />
      ) : users.error ? (
        <ErrorState error={users.error} />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>User</Th>
              <Th>Person record</Th>
              <Th>Role</Th>
              {facilitiesOn && <Th>Facilities role</Th>}
              <Th>Last sign-in</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {users.data!.map((membership) => (
              <tr key={membership.id}>
                <Td>
                  <p className="font-medium text-slate-900">
                    {membership.user.name}
                    {membership.user.id === me?.user.id && <span className="ml-2 text-xs font-normal text-slate-400">(you)</span>}
                    {membership.user.isPlatformAdmin && <Badge tone="violet" className="ml-2">Edvora</Badge>}
                  </p>
                  <p className="text-xs text-slate-500">
                    {membership.user.email}
                    {membership.user.usesGoogle ? <span className="ml-2 text-slate-400">· Google</span> : membership.user.hasPassword ? <span className="ml-2 text-slate-400">· Password</span> : null}
                  </p>
                </Td>
                <Td className="max-w-[14rem] text-xs">
                  {/* Requesters see devices through the active person record with the same email. */}
                  {membership.linkedPerson ? (
                    <span className="text-slate-700">
                      {membership.linkedPerson.preferredName || membership.linkedPerson.firstName} {membership.linkedPerson.lastName}
                      <span className="text-slate-400"> · {membership.linkedPerson.type.toLowerCase().replace('_', ' ')}</span>
                    </span>
                  ) : membership.role === 'REQUESTER' ? (
                    <span className="text-amber-700">No person with this email - they won’t see any devices</span>
                  ) : (
                    <span className="text-slate-400">None</span>
                  )}
                </Td>
                <Td className="w-44">
                  <Select value={membership.role} onChange={(e) => changeRole.mutate({ id: membership.id, role: e.target.value as TenantRole })}>
                    {(Object.keys(ROLE_LABEL) as TenantRole[]).map((role) => (
                      <option key={role} value={role}>
                        {ROLE_LABEL[role]}
                      </option>
                    ))}
                  </Select>
                </Td>
                {facilitiesOn && (
                  <Td className="w-52">
                    {membership.role === 'ADMIN' ? (
                      <span className="text-xs text-slate-500">Facilities admin, as a school administrator</span>
                    ) : (
                      <Select
                        value={membership.facilitiesRole ?? ''}
                        aria-label={`Facilities role for ${membership.user.name}`}
                        onChange={(e) => changeRole.mutate({ id: membership.id, facilitiesRole: (e.target.value || null) as FacilitiesRole | null })}
                      >
                        <option value="">Sends requests</option>
                        {FACILITIES_ROLES.map((role) => (
                          <option key={role} value={role}>
                            {FACILITIES_ROLE_LABEL[role]}
                          </option>
                        ))}
                      </Select>
                    )}
                  </Td>
                )}
                <Td className="whitespace-nowrap text-slate-500">{membership.user.lastLoginAt ? formatRelative(membership.user.lastLoginAt) : 'Never'}</Td>
                <Td className="text-right whitespace-nowrap">
                  {passwordsAllowed && membership.user.hasPassword && (
                    <Button size="sm" variant="ghost" icon={<KeyRound />} loading={reset.isPending && reset.variables?.id === membership.id} onClick={() => reset.mutate(membership)}>
                      Reset password
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" icon={<Trash2 />} onClick={() => setRemoving(membership)}>
                    Remove
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <div className="border-t border-slate-100 px-5 py-4">
        <dl className="grid gap-3 text-sm sm:grid-cols-3">
          {(Object.keys(ROLE_LABEL) as TenantRole[]).map((role) => (
            <div key={role}>
              <dt className="font-medium text-slate-800">{ROLE_LABEL[role]}</dt>
              <dd className="text-xs text-slate-500">{ROLE_DESCRIPTION[role]}</dd>
            </div>
          ))}
        </dl>
        {facilitiesOn && (
          <dl className="mt-4 grid gap-3 border-t border-slate-100 pt-4 text-sm sm:grid-cols-3">
            {FACILITIES_ROLES.map((role) => (
              <div key={role}>
                <dt className="font-medium text-slate-800">{FACILITIES_ROLE_LABEL[role]}</dt>
                <dd className="text-xs text-slate-500">{FACILITIES_ROLE_DESCRIPTION[role]}</dd>
              </div>
            ))}
            <p className="text-xs text-slate-500 sm:col-span-3">
              Everyone else at the school can send facilities requests. School administrators are facilities administrators too.
            </p>
          </dl>
        )}
      </div>

      <Modal
        open={adding}
        onClose={() => setAdding(false)}
        size="sm"
        title="Add a user"
        description={
          googleSchool
            ? 'People on your Google Workspace domains sign in with Google. No password is created.'
            : 'If they already have an Edvora account, they’ll get access to this school with their existing password.'
        }
        footer={
          <>
            <Button onClick={() => setAdding(false)}>Cancel</Button>
            <Button variant="primary" loading={add.isPending} onClick={() => add.mutate()}>
              Add user
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Email" required>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoFocus />
          </Field>
          <Field label="Name" required>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label="Role" hint={ROLE_DESCRIPTION[form.role]}>
            <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as TenantRole })}>
              {(Object.keys(ROLE_LABEL) as TenantRole[]).map((role) => (
                <option key={role} value={role}>
                  {ROLE_LABEL[role]}
                </option>
              ))}
            </Select>
          </Field>
          {facilitiesOn && form.role !== 'ADMIN' && (
            <Field
              label="Facilities role"
              hint={form.facilitiesRole ? FACILITIES_ROLE_DESCRIPTION[form.facilitiesRole] : 'Everyone can send facilities requests. Give a role to people who work them.'}
            >
              <Select value={form.facilitiesRole} onChange={(e) => setForm({ ...form, facilitiesRole: e.target.value as FacilitiesRole | '' })}>
                <option value="">Sends requests</option>
                {FACILITIES_ROLES.map((role) => (
                  <option key={role} value={role}>
                    {FACILITIES_ROLE_LABEL[role]}
                  </option>
                ))}
              </Select>
            </Field>
          )}
        </div>
      </Modal>
      <ConfirmDialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={() => remove.mutate()}
        loading={remove.isPending}
        title="Remove access?"
        description={`${removing?.user.email} will no longer be able to sign in to this school.`}
        confirmLabel="Remove"
      />
      {tempPassword && <TemporaryPasswordNotice {...tempPassword} onClose={() => setTempPassword(null)} />}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Audit log
// ---------------------------------------------------------------------------

function AuditLog() {
  const [search, setSearch] = useState('');
  const debounced = useDebouncedValue(search, 300);
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [debounced]);
  const logs = useQuery({
    queryKey: ['audit-logs', debounced, page],
    queryFn: () => api.get<Page<AuditEntry>>('/audit-logs', { search: debounced, page, pageSize: 50 }),
    placeholderData: keepPreviousData,
  });

  return (
    <Card>
      <CardHeader title="Audit log" description="Every change made in this school’s environment" />
      <div className="border-b border-slate-100 p-3">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search activity or person" className="w-full sm:w-80" />
      </div>
      {logs.isLoading ? (
        <PageLoader />
      ) : logs.data!.items.length === 0 ? (
        <EmptyState title="No matching activity" />
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th>When</Th>
                <Th>Who</Th>
                <Th>What happened</Th>
                <Th>IP address</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {logs.data!.items.map((entry) => (
                <tr key={entry.id}>
                  <Td className="whitespace-nowrap text-slate-500">{formatDateTime(entry.createdAt)}</Td>
                  <Td className="whitespace-nowrap">{entry.actorName ?? 'System'}</Td>
                  <Td>
                    <p className="text-slate-800">{entry.summary}</p>
                    <p className="font-mono text-xs text-slate-400">{entry.action}</p>
                  </Td>
                  <Td className="font-mono text-xs text-slate-500">{entry.ipAddress ?? '—'}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Pagination page={page} pageSize={50} total={logs.data!.total} onPage={setPage} />
        </>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// My account
// ---------------------------------------------------------------------------

function AccountSettings() {
  const { me } = useAuth();
  const toast = useToast();
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [error, setError] = useState('');
  const change = useMutation({
    mutationFn: () => api.post('/auth/change-password', { currentPassword: form.currentPassword, newPassword: form.newPassword }),
    onSuccess: () => {
      setForm({ currentPassword: '', newPassword: '', confirm: '' });
      setError('');
      toast.success('Password changed. Other sessions were signed out.');
    },
    onError: (err) => setError(errorMessage(err)),
  });

  if (!me?.user.hasPassword) {
    return (
      <Card>
        <CardHeader title="My account" description={`${me?.user.name} · ${me?.user.email}`} />
        <div className="max-w-md p-5">
          <Alert tone="blue" title="You sign in with Google">
            There’s no Edvora password to change. Manage your password and two-step verification in your Google account.
          </Alert>
        </div>
      </Card>
    );
  }

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (form.newPassword !== form.confirm) {
      setError('The new passwords don’t match');
      return;
    }
    change.mutate();
  };

  return (
    <Card>
      <CardHeader title="My account" description={`${me.user.name} · ${me.user.email}`} />
      <form onSubmit={submit} className="max-w-md space-y-4 p-5">
        {me.authMethod === 'GOOGLE' && <Alert tone="blue">You signed in with Google this time. Your Edvora password still works where passwords are allowed.</Alert>}
        {error && <Alert tone="red">{error}</Alert>}
        <Field label="Current password">
          <Input type="password" autoComplete="current-password" value={form.currentPassword} onChange={(e) => setForm({ ...form, currentPassword: e.target.value })} required />
        </Field>
        <Field label="New password" hint="At least 10 characters">
          <Input type="password" autoComplete="new-password" minLength={10} value={form.newPassword} onChange={(e) => setForm({ ...form, newPassword: e.target.value })} required />
        </Field>
        <Field label="Confirm new password">
          <Input type="password" autoComplete="new-password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} required />
        </Field>
        <Button type="submit" variant="primary" loading={change.isPending}>
          Change password
        </Button>
      </form>
    </Card>
  );
}

export function SettingsPage() {
  const { can } = useAuth();
  const isAdmin = can('ADMIN');
  const [state, setState] = useUrlState({ tab: 'school' });
  const tabs = [
    // Requesters only manage their own account.
    ...(can('VIEWER') ? [{ value: 'school', label: 'School' }] : []),
    ...(can('TECHNICIAN')
      ? [
          { value: 'models', label: 'Makes & models' },
          { value: 'software', label: 'Software' },
        ]
      : []),
    ...(isAdmin
      ? [
          { value: 'categories', label: 'Categories' },
          { value: 'users', label: 'Users & roles' },
          { value: 'sign-in', label: 'Sign-in & branding' },
          { value: 'checkouts', label: 'Checkouts & loaners' },
          { value: 'billing', label: 'Damage billing' },
          { value: 'email-templates', label: 'Email templates' },
          { value: 'audit', label: 'Audit log' },
        ]
      : []),
    { value: 'account', label: 'My account' },
  ];
  const tab = tabs.some((t) => t.value === state.tab) ? state.tab : tabs[0].value;

  return (
    <>
      <PageHeader title="Settings" />
      <Tabs className="mb-6" value={tab} onChange={(value) => setState({ tab: value })} tabs={tabs} />
      {tab === 'school' && (
        <div className="space-y-6">
          <SchoolSettings />
          <CampusNetworkSettings />
        </div>
      )}
      {tab === 'models' && <CatalogSettings />}
      {tab === 'software' && <SoftwareSettings />}
      {tab === 'categories' && <CategorySettings />}
      {tab === 'users' && <UserSettings />}
      {tab === 'sign-in' && <SignInSettings />}
      {tab === 'checkouts' && <CheckoutSettingsPanel />}
      {tab === 'billing' && <DamageBillingSettings />}
      {tab === 'email-templates' && <EmailTemplateSettings />}
      {tab === 'audit' && <AuditLog />}
      {tab === 'account' && <AccountSettings />}
    </>
  );
}
